"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

const LONG_STROKE = "̶";
const TILDE_OVERLAY = "̴";

function applyCombiningMark(text: string, mark: string): string {
  return [...text].map((char) => `${char}${mark}`).join("");
}

/** English strings. A translated content file overrides these through `ui`. */
export const STRIKETHROUGH_TEXT_GENERATOR_STRINGS = {
  yourText: "Your text",
  placeholder: "Type or paste text to strike through",
  copied: "Copied!",
  copy: "Copy",
  resultPlaceholder: "Your result will appear here",
  lineLabel: "Strikethrough (long stroke)",
  tildeLabel: "Tilde strikethrough (wavy)",
} as const;

function ResultCard({
  copied,
  label,
  onCopy,
  value,
  strings,
}: {
  copied: boolean;
  label: string;
  onCopy: () => void;
  value: string;
  strings: typeof STRIKETHROUGH_TEXT_GENERATOR_STRINGS;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between gap-3">
        <p className="font-medium text-foreground text-sm">{label}</p>
        <button
          className="rounded-lg border border-border bg-background px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={value.length === 0}
          onClick={onCopy}
          type="button"
        >
          {copied ? strings.copied : strings.copy}
        </button>
      </div>
      <p className="min-h-[2rem] whitespace-pre-wrap break-words text-base text-foreground leading-relaxed">
        {value.length > 0 ? value : strings.resultPlaceholder}
      </p>
    </div>
  );
}

export default function StrikethroughTextGeneratorWidget() {
  const strings = useToolUi(STRIKETHROUGH_TEXT_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [copiedCard, setCopiedCard] = useState<string | null>(null);

  const lineStrike = applyCombiningMark(input, LONG_STROKE);
  const tildeStrike = applyCombiningMark(input, TILDE_OVERLAY);

  function handleCopy(card: string, value: string) {
    if (value.length === 0) {
      return;
    }
    writeToClipboard(value).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedCard(card);
      setTimeout(() => setCopiedCard(null), 2000);
    });
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="strike-input"
        >
          {strings.yourText}
        </label>
        <textarea
          className="min-h-[120px] w-full resize-y rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="strike-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.placeholder}
          value={input}
        />
      </div>

      {/* Results */}
      <section className="space-y-4">
        <ResultCard
          copied={copiedCard === "line"}
          label={strings.lineLabel}
          onCopy={() => handleCopy("line", lineStrike)}
          strings={strings}
          value={input.length > 0 ? lineStrike : ""}
        />
        <ResultCard
          copied={copiedCard === "tilde"}
          label={strings.tildeLabel}
          onCopy={() => handleCopy("tilde", tildeStrike)}
          strings={strings}
          value={input.length > 0 ? tildeStrike : ""}
        />
      </section>
    </>
  );
}
